import { Alert, Button, Card, Form, Input, Space, Tree, Typography } from "antd";
import type { DataNode } from "antd/es/tree";
import type { Key } from "react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Pagination as ZhPagination, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { useUserStore } from "@/views/stores";

export interface DepartmentNode {
    id: number | string;
    data?: { name?: string };
    childNodes?: DepartmentNode[];
}

export interface DepartmentTreeResult {
    rootNode?: DepartmentNode[];
}

export interface OrganizationPerson {
    id?: number | string;
    person?: {
        id?: number | string;
        employeeId?: string;
        name?: string;
        position?: string;
        mobile?: string;
        mail?: string;
    };
    depts?: Array<Array<{ name?: string }>>;
}

export interface OrganizationPersonPageResult {
    list?: OrganizationPerson[];
    total?: number;
}

export interface OrganizationPersonQuery {
    page: number;
    size: number;
    deptId: number;
    param?: string;
}

type OrganizationPersonFilterValues = {
    param?: string;
};

export interface OrganizationalStructurePageProps {
    loadDepartmentTree?: () => Promise<DepartmentTreeResult>;
    loadPeople?: (query: OrganizationPersonQuery) => Promise<OrganizationPersonPageResult>;
    permissions?: string[];
}

const defaultQuery: OrganizationPersonQuery = { page: 1, size: 20, deptId: 1 };

/** 复用旧 Vue `/admin/dept/tree` 接口。 */
export async function loadDepartmentTree(): Promise<DepartmentTreeResult> {
    const response = await request.service.get<DepartmentTreeResult>("/admin/dept/tree");
    return response.data;
}

/** 复用旧 Vue `/admin/person/paging` 接口，默认查询根部门人员。 */
export async function loadOrganizationPeople(query: OrganizationPersonQuery): Promise<OrganizationPersonPageResult> {
    const response = await request.service.get<OrganizationPersonPageResult>("/admin/person/paging", {
        params: query
    });
    return response.data;
}

const getTreeData = (nodes: DepartmentNode[]): DataNode[] =>
    nodes.map(node => ({
        key: String(node.id),
        title: node.data?.name || "未命名部门",
        children: getTreeData(node.childNodes || [])
    }));

const getDepartmentNames = (depts: OrganizationPerson["depts"] = []) =>
    depts
        .map(path => path[path.length - 1]?.name)
        .filter(Boolean)
        .join("；") || DEFAULT_VALUE;

const formatPersonValue = (value: unknown): string =>
    value === undefined || value === null || value === "" ? DEFAULT_VALUE : String(value);

/** 旧 Vue PersonVo 将编辑所需员工 ID 放在 person.id，兼容迁移期间的顶层 ID。 */
const getPersonId = (person: OrganizationPerson): number | string | undefined => person.person?.id ?? person.id;

/** 系统管理-组织架构页面，覆盖 Vue 树和人员分页主流程。 */
export function OrganizationalStructurePage({
    loadDepartmentTree: loadTree = loadDepartmentTree,
    loadPeople = loadOrganizationPeople,
    permissions
}: OrganizationalStructurePageProps) {
    const [tree, setTree] = useState<DepartmentNode[]>([]);
    const [selectedDepartmentId, setSelectedDepartmentId] = useState(defaultQuery.deptId);
    const [people, setPeople] = useState<OrganizationPerson[]>([]);
    const [total, setTotal] = useState(0);
    const [expandedKeys, setExpandedKeys] = useState<Key[]>([]);
    const [collapsed, setCollapsed] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [query, setQuery] = useState<OrganizationPersonQuery>(defaultQuery);
    const [filterForm] = Form.useForm<OrganizationPersonFilterValues>();
    const location = useLocation();
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const storeBanButton = useUserStore(state => state.banButton);
    const currentPermissions = permissions || storePermissions;
    // 保留 Vue v-auth 的特殊权限：封禁/提示权限会保留按钮，封禁权限同时禁用按钮。
    const canEdit = currentPermissions.some(permission =>
        ["ROLE_EMPLOYEE_W", "ROLE_BUTTON_BAN", "ROLE_BUTTON_TIP"].includes(permission)
    );
    const editDisabled = permissions ? currentPermissions.includes("ROLE_BUTTON_BAN") : storeBanButton;

    const treeData = useMemo(() => getTreeData(tree), [tree]);

    useEffect(() => {
        let disposed = false;
        void loadTree()
            .then(departmentResult => {
                if (!disposed) {
                    const rootNode = departmentResult.rootNode || [];
                    setTree(rootNode);
                    setExpandedKeys(rootNode.map(node => String(node.id)));
                }
            })
            .catch(() => {
                if (!disposed) {
                    setErrorMessage("组织架构加载失败");
                    setTree([]);
                }
            });
        return () => {
            disposed = true;
        };
    }, [loadTree]);

    useEffect(() => {
        let disposed = false;
        void loadPeople(query)
            .then(result => {
                if (!disposed) {
                    setPeople(result.list || []);
                    setTotal(result.total || 0);
                    setErrorMessage(null);
                }
            })
            .catch(() => {
                if (!disposed) {
                    setErrorMessage("人员列表加载失败");
                    setPeople([]);
                    setTotal(0);
                }
            });
        return () => {
            disposed = true;
        };
    }, [loadPeople, query]);

    // 将旧 Vue ProTable 的人员关键词筛选转换为人员分页接口参数。
    const submitFilters = ({ param }: OrganizationPersonFilterValues) => {
        setErrorMessage(null);
        setQuery(currentQuery => ({
            page: 1,
            size: 20,
            deptId: currentQuery.deptId,
            ...(param ? { param } : {})
        }));
    };

    // 清空人员关键词并保留当前部门，恢复旧 Vue 分页参数。
    const resetFilters = () => {
        filterForm.resetFields();
        setQuery(currentQuery => ({ page: 1, size: 20, deptId: currentQuery.deptId }));
    };

    // 用 zhihao-ui-react 列契约承接 Vue 组织成员的嵌套人员字段和权限操作。
    const columns: TableColumnProps<OrganizationPerson>[] = [
        {
            key: "employeeId",
            label: "工号",
            prop: "employeeId",
            render: ({ row }) => formatPersonValue(row.person?.employeeId)
        },
        {
            key: "name",
            label: "姓名",
            prop: "name",
            render: ({ row }) => formatPersonValue(row.person?.name)
        },
        { key: "depts", label: "部门", prop: "depts", render: ({ row }) => getDepartmentNames(row.depts) },
        {
            key: "position",
            label: "职务",
            prop: "position",
            render: ({ row }) => formatPersonValue(row.person?.position)
        },
        {
            key: "mobile",
            label: "手机号",
            prop: "mobile",
            render: ({ row }) => formatPersonValue(row.person?.mobile)
        },
        {
            key: "mail",
            label: "邮箱",
            prop: "mail",
            render: ({ row }) => formatPersonValue(row.person?.mail)
        },
        {
            key: "operation",
            label: "操作",
            prop: "operation",
            render: ({ row }: { row: OrganizationPerson }) => {
                const personId = getPersonId(row);
                return canEdit && personId != null ? (
                    <Space>
                        <Button
                            aria-label={`编辑员工${row.person?.name || personId}`}
                            disabled={editDisabled}
                            onClick={() => {
                                // 两套路由都沿用 Vue 路径：系统管理使用 query，平台管理使用 path 参数。
                                const editPath = location.pathname.startsWith("/manageCenter")
                                    ? `/manageCenter/systemManage/organizationalStructure/edit/${personId}`
                                    : `/systemMangement/systemManage/organizationalStructure/edit?id=${personId}`;
                                void navigate(editPath);
                            }}
                            type="link"
                        >
                            编辑
                        </Button>
                    </Space>
                ) : null;
            }
        }
    ];

    return (
        <section className="flex h-full min-h-full flex-col" aria-label="organizational-structure-page">
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <Typography.Title className="mb-4 mt-0" level={4}>
                    组织架构
                </Typography.Title>
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                <div className="flex min-h-0 flex-1 gap-6">
                    <aside className="w-64 shrink-0 overflow-auto border-r pr-4" aria-label="department-tree">
                        <div className="mb-3 flex items-center justify-between">
                            <Typography.Text strong>部门</Typography.Text>
                            <Button
                                size="small"
                                type="link"
                                onClick={() => {
                                    const nextCollapsed = !collapsed;
                                    setCollapsed(nextCollapsed);
                                    setExpandedKeys(nextCollapsed ? [] : tree.map(node => String(node.id)));
                                }}
                            >
                                {collapsed ? "展开全部" : "收起全部"}
                            </Button>
                        </div>
                        <Tree
                            blockNode
                            expandedKeys={expandedKeys}
                            treeData={treeData}
                            onExpand={keys => setExpandedKeys(keys)}
                            onSelect={keys => {
                                const nextId = keys[0];
                                if (nextId == null) return;
                                const deptId = Number(nextId);
                                setSelectedDepartmentId(deptId);
                                setQuery(currentQuery => ({ ...currentQuery, page: 1, deptId }));
                            }}
                        />
                    </aside>
                    <div className="min-w-0 flex-1">
                        <ZhTable<OrganizationPerson>
                            data={people}
                            columns={columns}
                            emptyText="暂无组织成员"
                            pagination={false}
                            rowKey={record => record.id || record.person?.employeeId || "person"}
                            isShowResetBtn={false}
                            isShowSearchBtn={false}
                            tableHeader={
                                <div className="admin-table-composed-header">
                                    <Form
                                        className="mb-4"
                                        form={filterForm}
                                        layout="inline"
                                        onFinish={values => submitFilters(values)}
                                    >
                                        <Form.Item label="姓名、手机号或邮箱" name="param">
                                            <Input placeholder="姓名、手机号或邮箱" />
                                        </Form.Item>
                                        <Space>
                                            <Button htmlType="submit" type="primary">
                                                查询人员
                                            </Button>
                                            <Button onClick={resetFilters}>重置</Button>
                                        </Space>
                                    </Form>

                                    <div className="mb-2">当前部门：{selectedDepartmentId}</div>
                                </div>
                            }
                            append={
                                <div className="admin-table-composed-pagination">
                                    <ZhPagination
                                        current={query.page}
                                        pageSize={query.size}
                                        total={total}
                                        pageSizeOptions={[20, 50, 100]}
                                        showSizeChanger
                                        // 沿用 Vue 分页参数，切换页大小时回到第 1 页并保留部门。
                                        onChange={(page, pageSize) =>
                                            setQuery(currentQuery => ({
                                                ...currentQuery,
                                                page: pageSize !== currentQuery.size ? 1 : page,
                                                size: pageSize
                                            }))
                                        }
                                    />
                                </div>
                            }
                        />
                    </div>
                </div>
            </Card>
        </section>
    );
}

export default OrganizationalStructurePage;
